import React, { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  CampaignNPC,
  VendorInvItem,
} from "@dndModels";

import DandDAPI from "@utilities/smart_api";
import { makeStyles } from "@utilities/theme_utilities";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CommonUtilities from "@utilities/common_utilities";

import FlexBox from "@components/layout/FlexBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import VendorInventoryItemInput from "./VendorInventoryItemInput";

interface Props {
  campaign: Campaign;
  npc: CampaignNPC;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function VendorInventoryInput({
  campaign,
  npc
}: Props) {
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<VendorInvItem | undefined>(undefined);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderInventory = () => {
    return (
      <FlexBox direction="column" style={{ width: "100%" }}>
        <FlexBox className="header centered">Inventory</FlexBox>
        <FlexBox className="padded">
          <AddButton label="Add Item" 
            disabled={processing}
            onClick={ async () => {
              const new_item = new VendorInvItem();
              setProcessing(true);
              npc.inventory.push(new_item);
              await api.upsertObject("campaign_npc", npc);
              setSelected(new_item);
              setProcessing(false);
            }}
          />
        </FlexBox>
        <FlexBox>
          <Grid container spacing={0} direction="row">
            { npc.inventory.map(inv_item => {
              return (
                <Grid size={4} key={inv_item.true_id}>
                  <div className="padded">
                    <FlexBox direction="row" className={cx(classes.root)}>
                      <FlexBox className="header centered clickable" onClick={() => setSelected(selected && selected.true_id === inv_item.true_id ? undefined : inv_item) }>{ inv_item.name }</FlexBox>
                      <FlexBox xs_expand={0}>
                        <div style={{ float: "right"}}>
                          <DeleteButton
                            no_fab
                            obj_name={inv_item.name}
                            onClick={ async () => {
                              if (selected && selected.true_id === inv_item.true_id) {
                                setSelected(undefined);
                              }
                              npc.inventory = npc.inventory.filter(o => o.true_id !== inv_item.true_id);
                              await api.upsertObject("campaign_npc", npc);
                              setPing(!ping);
                            }} 
                          />
                        </div>
                      </FlexBox>
                    </FlexBox>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </FlexBox>
        { renderSelected() }
      </FlexBox>
    );
  }

  const renderSelected = () => {
    if (selected) {
      return (
        <FlexBox>
          <VendorInventoryItemInput
            campaign={campaign}
            npc={npc} 
            inv_item={selected}
          />
        </FlexBox>
      );
    }
    return null;
  }

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={open}
          width={"80%"}
          onClose={() => { setOpen(false); }}>
          { renderInventory() }
        </ModalOrDrawer>
      </>
    )
  }

  return (
    <FlexBox>
      <div className="padded">
        <FlexBox className={cx(classes.root, "header centered clickable")} onClick={() => { setOpen(true); }}>
          Inventory
        </FlexBox>
        { renderExtras() }
      </div>
    </FlexBox>
  );
}
